import { makeStyles } from '@material-ui/styles'
import { pick } from 'ramda'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useReactRouter from 'use-react-router'
import { listTenants } from 'account/components/userManagement/tenants/new-actions'
import { tenantsById } from 'account/components/userManagement/tenants/selectors'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { imagesVsSnapshotsDoc } from 'app/docLinks'
import DeleteImage from 'app/plugins/openstack/components/images/DeleteImage'
import { getVmSnapshotColumns, gridSearchFn } from 'app/plugins/openstack/components/images/helpers'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import CreateButton from 'core/components/buttons/CreateButton'
import DocumentMeta from 'core/components/DocumentMeta'
import ExternalLink from 'core/components/ExternalLink'
import { MessageTypes } from 'core/components/notifications/model'
import PollingData from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import ListContainer from 'core/containers/ListContainer'
import Badge, { BadgeVariant } from 'core/elements/badge/Badge'
import Button from 'core/elements/button'
import { createGridLinkCell } from 'core/elements/grid/cells/GridLinkCell'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { useLocalNetworkAccessAllowed } from 'core/hooks/useLocalNetworkAccessAllowed'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScrollPosition from 'core/hooks/useScrollPosition'
import { useToast } from 'core/providers/ToastProvider'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import { isOpenCloud, isReadonlyUser } from 'core/utils/helpers'
import { isAdminRole } from 'core/utils/rbac'
import { routeManager } from 'core/utils/routes'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import { Labels } from 'k8s/components/common/entity/labels-and-annotations/LabelsOrAnnotations'
import DataKeys from 'k8s/DataKeys'
import { listBlueprints, listResmgrHosts } from 'openstack/components/infrastructure/actions'
import {
  imageLibraryStoresSelector,
  resmgrHostsSelector,
} from 'openstack/components/infrastructure/selectors'
import { listVolumeSnapshots } from 'openstack/components/storage/snapshots/actions'
import { volumeSnapshotsByIdSelector } from 'openstack/components/storage/snapshots/selectors'
import { listVolumesForImages } from 'openstack/components/storage/volumes/actions'
import { volumesByImageIdSelector } from 'openstack/components/storage/volumes/selectors'
import { IVolumeDetailsPageTabs } from 'openstack/components/storage/volumes/volume-details/model'
import { listVirtualMachinesLite } from 'openstack/components/vms/actions'
import { virtualMachinesLiteByIdSelector } from 'openstack/components/vms/selectors'
import { hasPermissions, humanReadableSize } from 'openstack/helpers'
import { durationBetweenDates } from 'utils/misc'
import AcceptSharedImagesDrawer from './AcceptSharedImagesDrawer'
import AddImageModal from './AddImageModal'
import { deleteImage, getHostGlanceEndpoint, listImages, testHostGlance } from './actions'
import BackingVolumesCell from './BackingVolumesCell'
import { checkImageLibraryRequiredActions } from './checkImageLibraryRequiredActions'
import { TPMModelText } from './constants'
import DeleteVmSnapshot from './DeleteVmSnapshot'
import { ImageDetailsPageTabs } from './details/model'
import EditImageMetadataModal from './EditImageMetadataModal'
import EditImageModal from './EditImageModal'
import ImageStatusInfo from './ImageStatusInfo'
import ImageStoresCell from './ImageStoresCell'
import ImportImageModal from './ImportImageModal'
import { canCopyToAllStores, collectConfiguredStoreIds, collectNewFailures } from './imageStores'
import { ImagePageTabs } from './model'
import SyncImagesToStoresDialog from './SyncImagesToStoresDialog'
import { imagesSelector } from './selectors'
import useImportProgress from './useImportProgress'

type ModelDataKey = DataKeys.Images
type SelectorModel = ArrayElement<ReturnType<typeof imagesSelector>>
type ActionParams = InferActionParams<typeof listImages>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>('Images', listTablePrefs)

export const TableHeaderTooltip = ({ message }) => {
  return <div>{message}</div>
}

const getImageStatusVariant = (status: string): BadgeVariant => {
  const map: Record<string, BadgeVariant> = {
    active: 'success',
    queued: 'warning',
    saving: 'warning',
    deactivated: 'warning',
    killed: 'error',
    deleted: 'error',
    pending_delete: 'error',
  }
  return map[status] ?? 'unknown'
}

const ImageStatusCell = ({ value }: { value: string }) => (
  <Badge text={value} variant={getImageStatusVariant(value)} />
)

const searchTargets = [
  'name',
  'id',
  'status',
  'visibility',
  'owner',
  'disk_format',
  'tags',
  'properties',
  'imageTypeDisplay',
]
const searchFunctions = [
  {
    property: 'properties',
    searchFn: (value, searchString) => gridSearchFn(value, searchString),
  },
]

const COLUMN_TOOLTIPS = {
  backingVolumes: "Cinder volumes storing this image's data - respective to each image store.",
  virtualDiskSize: 'The size of the VM disk that will get created from this image',
  fileSize: 'The size of the snapshot file that is stored in the Image Library',
}

type BackingVolumesById = ReturnType<typeof volumesByImageIdSelector>

export const imageColumns = (
  imageListPage = true,
  tenantMap: Record<string, { name: string }> = {},
  volumesByImageId: BackingVolumesById = {},
  isAdmin = false,
  classes: { backingVolumesGrid?: string } = {},
): GridViewColumn<SelectorModel>[] => [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
    CellComponent: createGridLinkCell({
      routeToFn: ({ id, name }) =>
        !!name
          ? routeManager.routes.openstack.imageDetails.path({
              id,
              detailsTab: ImageDetailsPageTabs.Overview,
            })
          : '',
    }),
  },
  {
    key: 'id',
    label: 'UUID',
    display: false,
  },
  {
    key: 'status',
    label: <TitleWithInfoIcon titleText="Status" tooltipMsg={<ImageStatusInfo />} />,
    CellComponent: ImageStatusCell,
  },
  {
    key: 'storeStatuses',
    label: 'Stores',
    disableSorting: true,
    render: (value) => <ImageStoresCell storeStatuses={value} />,
  },
  {
    key: 'imageTypeDisplay',
    label: 'Type',
  },
  {
    key: 'visibility',
    label: 'Visibility',
  },
  {
    key: 'owner',
    label: (
      <TitleWithInfoIcon
        titleText="Owner Tenant"
        tooltipMsg="The tenant that owns this image or where this image is created from."
      />
    ),
    render: (ownerId) => tenantMap[ownerId]?.name || ownerId || '',
  },
  // IMGLIST-FR-040a: Backing Volumes column requires an admin-scoped all-tenants
  // Cinder query, so it is omitted entirely (not shown empty) for non-admins.
  ...(isAdmin
    ? [
        {
          key: 'backingVolumes',
          label: (
            <TitleWithInfoIcon
              titleText="Backing Volumes"
              tooltipMsg={<TableHeaderTooltip message={COLUMN_TOOLTIPS.backingVolumes} />}
            />
          ),
          disableSorting: true,
          render: (_value, item) => (
            <BackingVolumesCell volumesByImageId={volumesByImageId} imageId={item.id} />
          ),
        },
      ]
    : []),
  {
    key: 'protected',
    label: 'Protected',
    render: (val) => (val ? 'True' : 'False'),
  },
  {
    key: 'disk_format',
    label: 'Disk Format',
  },
  {
    key: 'virtual_size',
    label: (
      <TitleWithInfoIcon
        titleText="Virtual Disk Size"
        tooltipMsg={<TableHeaderTooltip message={COLUMN_TOOLTIPS.virtualDiskSize} />}
      />
    ),
    render: humanReadableSize,
  },
  {
    key: 'size',
    label: (
      <TitleWithInfoIcon
        titleText="File Size"
        tooltipMsg={<TableHeaderTooltip message={COLUMN_TOOLTIPS.fileSize} />}
      />
    ),
    render: humanReadableSize,
  },
  {
    key: 'hw_tpm_model',
    label: 'Virtual TPM 2.0',
    render: (value, item) =>
      item?.hw_tpm_version === '2.0' ? `Enabled (${TPMModelText[value]})` : '',
  },
  {
    key: 'tags',
    label: 'Tags',
    render: (tags) => tags?.join(', '),
  },
  {
    key: 'properties',
    label: 'Properties',
    disableSorting: true,
    ...(imageListPage
      ? { CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }) }
      : {
          render: (value) => (
            <Labels
              labels={value}
              variant="default"
              separator={'='}
              containerType={'table'}
              maxVisible={1}
              showMoreButton={true}
            />
          ),
        }),
  },
  {
    key: 'created_at',
    label: 'Age',
    render: (value) => durationBetweenDates({ labels: ['d', 'h'] })(value),
  },
]

// const dropdownFilters = [
//   {
//     key: 'imageTypeDisplay',
//     label: 'Type',
//     FilterComponent: StringMultiDropdownFilter,
//     filterComponentProps: {
//       label: 'Type',
//     },
//     filterComponentOptionsPropName: 'dropdownOptions',
//     getOptionsFn: () => {
//       return [
//         {
//           label: 'VM Ephemeral Disk Snapshot',
//           value: 'VM Ephemeral Disk Snapshot',
//         },
//         {
//           label: 'VM Volume Snapshot',
//           value: 'VM Volume Snapshot',
//         },
//       ]
//     },
//     equalityComparerFn: (item, values) => {
//       return values?.includes(item?.imageTypeDisplay)
//     },
//   },
// ]

export default function ImagesListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { history, location, match } = useReactRouter()
  const { tab } = match.params

  const session = useAppSelector((state) => state[sessionStoreKey]) as SessionState
  const { features } = session
  const isAdmin = useMemo(() => isAdminRole(session), [session])
  const classes = useStyles()
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const scrollY = useScrollPosition()

  const offset = useMemo(() => {
    return { vertical: 8 + scrollY, horizontal: 0 }
  }, [scrollY])

  const [selectedImage, setSelectedImage] = useState<SelectorModel>(null)
  const [showEditImageModal, setShowEditImageModal] = useState<boolean>(false)
  const [showEditMetadataModal, setShowEditMetadataModal] = useState<boolean>(false)
  const [showAcceptDrawer, setShowAcceptDrawer] = useState(false)
  const [needToValidateCert, setNeedToValidateCert] = useState<boolean>(false)
  const [glanceHostNotReachable, setGlanceHostNotReachable] = useState<boolean>(false)
  const [hostEndpoint, setHostEndpoint] = useState('')
  const isBrowserLocalNetworkAccessAllowed = useLocalNetworkAccessAllowed()

  const {
    message,
    loading,
    reload,
    refreshing: imagesRefreshing,
  } = useListAction(listImages, {
    params,
  })
  const data = useAppSelector(imagesSelector)
  const showToast = useToast()
  const notifiedFailuresRef = useRef<Set<string>>(new Set())

  // Images still copying to one or more stores — these get polled for live progress.
  const importingIds = useMemo(
    () =>
      data
        .filter((image) => image?.storeStatuses?.some((s) => s.state === 'importing'))
        .map((image) => image.id),
    [data],
  )
  // Refreshes each importing image into the cache every few seconds; the Stores column (which reads
  // from the cache) updates on its own, and polling stops once nothing is importing.
  useImportProgress(importingIds)

  // Announce each store-import failure once (deduped by image + failed-store set across polls).
  useEffect(() => {
    const notices = collectNewFailures(data, notifiedFailuresRef.current)
    notices.forEach((notice) => {
      notifiedFailuresRef.current.add(notice.signature)
      showToast(
        `Image "${notice.name}" failed to import to: ${notice.failed.join(', ')}`,
        MessageTypes.warning,
      )
    })
  }, [data, showToast])

  const [showSyncDialog, setShowSyncDialog] = useState(false)
  const [syncImages, setSyncImages] = useState<{ id: string; name?: string }[]>([])

  // The configured image stores come from the blueprint's `imageLibraryStores` — the authoritative
  // list (includes stores that hold no images yet). It's cached in Redux and refreshed after a
  // blueprint save, so this read is cheap and stays in sync when stores change.
  useListAction(listBlueprints)
  const configuredStoreIds = useAppSelector(imageLibraryStoresSelector)

  // Stores to compare each image against: the blueprint list unioned with every store observed
  // across the images — the latter is a fallback for when the blueprint isn't readable.
  const effectiveStoreIds = useMemo(
    () => collectConfiguredStoreIds(data, configuredStoreIds),
    [data, configuredStoreIds],
  )

  const images = useMemo(() => {
    return data.filter((image) => image?.imageType === 'image')
  }, [data])
  const vmSnapshots = useMemo(() => {
    return data.filter((image) =>
      ['ephemeralDiskSnapshot', 'vmVolumeSnapshot'].includes(image?.imageType),
    )
  }, [data])

  useListAction(listTenants)
  const tenantMap = useAppSelector(tenantsById)

  const { loading: loadingVms } = useListAction(listVirtualMachinesLite)
  const vmsById = useAppSelector(virtualMachinesLiteByIdSelector)

  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots)
  const volumeSnapshotsById = useAppSelector(volumeSnapshotsByIdSelector)

  const { loading: loadingHosts } = useListAction(listResmgrHosts)
  const hosts = useAppSelector(resmgrHostsSelector)
  const imageLibraryHosts = useMemo(() => {
    return hosts.filter((host) => !!host?.roles?.includes('image-library'))
  }, [hosts])

  // IMGLIST-FR-040: admin-gated — Cinder ignores project_id/tenant_id once all_tenants=true
  // is set, so no projectId param is passed; non-admins can't see this reserved-tenant data
  // regardless, so the call is skipped entirely for them (IMGLIST-FR-040a).
  const { loading: loadingVolumes, reload: reloadVolumesForImages } = useListAction(
    listVolumesForImages,
    {
      preRequisites: isAdmin,
    },
  )
  const volumesByImageId = useAppSelector(volumesByImageIdSelector)

  const asyncCall = useCallback(async () => {
    try {
      const endpoint = await getHostGlanceEndpoint()
      setHostEndpoint(endpoint)
      await testHostGlance(endpoint)

      setGlanceHostNotReachable(false)
      setNeedToValidateCert(false)
    } catch (err) {
      if (err?.err?.message === 'Network Error') {
        setNeedToValidateCert(true)
      } else if (err?.err?.code === 'ECONNABORTED') {
        setGlanceHostNotReachable(true)
      }
    }
  }, [getHostGlanceEndpoint, testHostGlance])

  useEffect(() => {
    asyncCall()
  }, [asyncCall])

  const vmSnapshotColumns: GridViewColumn<SelectorModel>[] = getVmSnapshotColumns({
    vmsById,
    volumeSnapshotsById,
  })

  const columns = useMemo(
    () => imageColumns(true, tenantMap, volumesByImageId, isAdmin, classes),
    [tenantMap, volumesByImageId, isAdmin, classes],
  )

  const actionRequiredIssues = useMemo(
    () =>
      checkImageLibraryRequiredActions({
        needToValidateCert,
        isBrowserLocalNetworkAccessAllowed,
        glanceHostNotReachable,
        hostEndpoint,
      }),
    [needToValidateCert, hostEndpoint, isBrowserLocalNetworkAccessAllowed, glanceHostNotReachable],
  )

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      label: 'Edit',
      icon: 'edit',
      cond: (selectedItems) => selectedItems.length === 1,
      disabledTooltip: 'You can only edit one image at a time',
      handleAction: ([image]) => {
        setSelectedImage(image)
        setShowEditImageModal(true)
      },
      refreshAfterSuccess: true,
      permissionsPath: ['glance', 'modify_image:PATCH:/v2/images/{image_id}'],
    },
    {
      cond: (selectedItems) => selectedItems.length === 1 && selectedItems.every((item) => !!item),
      disabledTooltip: 'You can only edit properties for a single image at a time',
      label: 'Edit Properties',
      icon: 'tags',
      handleAction: ([image]) => {
        setSelectedImage(image)
        setShowEditMetadataModal(true)
      },
      refreshAfterSuccess: true,
      permissionsPath: ['glance', 'modify_image:PATCH:/v2/images/{image_id}'],
    },
    {
      label: 'Copy to all stores',
      icon: 'copy',
      cond: (selectedItems) => canCopyToAllStores(selectedItems, effectiveStoreIds),
      disabledTooltip:
        'Available when more than one store is configured and the selected image(s) are ' +
        'missing from a store or failed to copy.',
      handleAction: (selectedItems) => {
        setSyncImages(selectedItems.map((image) => ({ id: image.id, name: image.name })))
        setShowSyncDialog(true)
      },
      // No refreshAfterSuccess (unlike Edit / Edit Properties above): the copy runs in the
      // background and the Stores column is kept live by useImportProgress, so an immediate list
      // refresh would show nothing new.
      permissionsPath: ['glance', 'copy_image:POST:/v2/images/{image_id}/import'],
    },
  ]

  const hideImageUploadButton = isOpenCloud()

  return (
    <>
      <DocumentMeta title="Images" />
      <PollingData
        hidden
        loading={loading}
        onReload={() => {
          return new Promise<void>((resolve) => {
            reload(true, false)
            asyncCall()
            resolve()
          })
        }}
        refreshDuration={1000 * 30}
      />
      {/* <TopHeaderActionPortal>
        {actionRequiredIssues.length ? (
          <ActionRequiredModalTrigger
            issues={actionRequiredIssues}
            modalTitle="Action Required"
            modalIntroText="The following actions are required to access the Image Library Endpoint:"
            trigger={({ openWithEvent }) => (
              <Text variant="subtitle2" className={classes.warning} onClick={openWithEvent}>
                <FontAwesomeIcon className={classes.icon}>info-circle</FontAwesomeIcon>
                {glanceHostNotReachable
                  ? 'Image Library Host Not Reachable - Action Required'
                  : 'Action Required'}{' '}
                ({actionRequiredIssues.length})
              </Text>
            )}
          />
        ) : null}
      </TopHeaderActionPortal> */}
      <ImportImageModal addRoute={routeManager.routes.openstack.importImage} />
      {!isVmware && (
        <AddImageModal
          addRoute={routeManager.routes.openstack.addImage}
          actionRequiredIssues={actionRequiredIssues}
          glanceHostNotReachable={glanceHostNotReachable}
          imageLibraryPresent={!!imageLibraryHosts?.length}
        />
      )}
      {showEditImageModal && (
        <EditImageModal onClose={() => setShowEditImageModal(false)} rows={[selectedImage]} />
      )}
      {showEditMetadataModal && (
        <EditImageMetadataModal
          onClose={() => setShowEditMetadataModal(false)}
          rows={[selectedImage]}
        />
      )}
      {showSyncDialog && (
        <SyncImagesToStoresDialog
          open
          images={syncImages}
          title="Copy to all stores?"
          prompt={`Copy the selected ${syncImages.length} image(s) to all stores? Copying runs in the background.`}
          actionRequiredIssues={actionRequiredIssues}
          glanceHostNotReachable={glanceHostNotReachable}
          imageLibraryPresent={!!imageLibraryHosts?.length}
          onClose={() => setShowSyncDialog(false)}
        />
      )}
      <AcceptSharedImagesDrawer
        open={showAcceptDrawer}
        onClose={() => setShowAcceptDrawer(false)}
      />
      <Tabs route={routeManager.routes.openstack.images}>
        <Tab label="Images" value={ImagePageTabs.Images}>
          <ListContainer<ModelDataKey, SelectorModel>
            listPermsKeyPath={['glance', 'get_images:GET:/v2/images']}
            createPermsKeyPath={['glance', 'add_image:POST:/v2/images']}
            editPermsKeyPath={['glance', 'modify_image:PATCH:/v2/images/{image_id}']}
            deletePermsKeyPath={['glance', 'delete_image:DELETE:/v2/images/{image_id}']}
            dataKey={DataKeys.Images}
            searchTargets={searchTargets}
            searchFunctions={searchFunctions}
            uniqueIdentifier="id"
            loading={loading}
            loadingMessage={message}
            onRefresh={() => {
              reload(true)
              asyncCall()
              if (isAdmin) reloadVolumesForImages(true)
            }}
            refreshing={imagesRefreshing}
            data={images}
            columns={columns}
            addUrl={
              hideImageUploadButton || isVmware
                ? null
                : routeManager.routes.openstack.addImage.path({ tab })
            }
            addText={hideImageUploadButton || isVmware ? null : 'Add Image'}
            getParamsUpdater={getParamsUpdater}
            batchActions={batchActions}
            deleteAction={deleteImage}
            deleteCond={(selectedItems) => !selectedItems?.some((item) => item?.protected)}
            deleteDisabledInfo="Unable to delete protected images"
            DeleteDialogComponent={DeleteImage}
            multiSelection
            extraHeaderContent={
              <>
                {/* TODO: How does Accept Shared Images work with permissions? */}
                {!isReadonlyUser() && (
                  <Button variant="secondary" onClick={() => setShowAcceptDrawer(true)}>
                    Accept Shared Images
                  </Button>
                )}
                {hideImageUploadButton ||
                isReadonlyUser() ||
                !hasPermissions(['glance', 'add_image:POST:/v2/images']) ? null : (
                  <CreateButton
                    variant="secondary"
                    onClick={() =>
                      history.push(routeManager.routes.openstack.importImage.path({ tab }))
                    }
                  >
                    Import With CLI
                  </CreateButton>
                )}
              </>
            }
            {...pick(listTablePrefs, params)}
          />
        </Tab>
        <Tab label="VM Snapshots" value={ImagePageTabs.Snapshots}>
          <ListContainer<ModelDataKey, SelectorModel>
            listPermsKeyPath={['glance', 'get_images:GET:/v2/images']}
            createPermsKeyPath={['glance', 'add_image:POST:/v2/images']}
            editPermsKeyPath={['glance', 'modify_image:PATCH:/v2/images/{image_id}']}
            deletePermsKeyPath={['glance', 'delete_image:DELETE:/v2/images/{image_id}']}
            dataKey={DataKeys.Images}
            label="VM Snapshots"
            searchTargets={searchTargets}
            searchFunctions={searchFunctions}
            uniqueIdentifier="id"
            loading={loading}
            loadingMessage={message}
            onRefresh={() => {
              reload(true)
              asyncCall()
            }}
            refreshing={imagesRefreshing}
            data={vmSnapshots}
            columns={vmSnapshotColumns}
            addUrl={
              hideImageUploadButton || isVmware
                ? null
                : routeManager.routes.openstack.addImage.path({ tab })
            }
            addText={hideImageUploadButton || isVmware ? null : 'Add Image'}
            getParamsUpdater={getParamsUpdater}
            batchActions={batchActions}
            deleteAction={deleteImage}
            deleteCond={(selectedItems) => !selectedItems?.some((item) => item?.protected)}
            deleteDisabledInfo="Unable to delete protected images"
            DeleteDialogComponent={DeleteVmSnapshot}
            multiSelection
            // dropdownFilters={dropdownFilters}
            extraHeaderContent={
              <>
                <ExternalLink url={imagesVsSnapshotsDoc}>Read About Image Types</ExternalLink>
                {hideImageUploadButton ||
                isReadonlyUser() ||
                !hasPermissions(['glance', 'add_image:POST:/v2/images']) ? null : (
                  <CreateButton
                    variant="secondary"
                    onClick={() =>
                      history.push(routeManager.routes.openstack.importImage.path({ tab }))
                    }
                  >
                    Import With CLI
                  </CreateButton>
                )}
              </>
            }
            {...pick(listTablePrefs, params)}
          />
        </Tab>
      </Tabs>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  icon: {
    color: theme.components.badge.error.color,
    marginRight: 8,
  },
  warning: {
    color: theme.components.badge.error.color,
    cursor: 'pointer',

    '& > span': {
      color: theme.components.badge.error.color,
    },
  },
}))
